// src/components/layout/Navbar.tsx
// ============================================================
// SALT — Main Navigation Bar
//
// Consumes AuthContext (user state) and GameContext (subdomain
// game detection). Renders:
//   Left:   Logo + Game Selector dropdown
//   Center: Dashboard / Leaderboard / Matches links (desktop)
//   Right:  Social icons (X, Discord) + Notification Bell + Auth CTA or Avatar menu
//   Mobile: Hamburger → MobileMenu drawer
//
// Desktop dropdown and mobile menu are portaled to document.body
// to avoid clipping from navbar backdrop-filter/overflow.
//
// Owns the useNotifications hook so notification state is shared
// between NotificationBell (desktop) and MobileMenu (mobile).
// ============================================================

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useGame } from '../../hooks/useGame';
import { useNotifications } from '../../hooks/useNotifications';
import axiosClient from '../../api/axiosClient';

// Layout sub-components
import NavLink from './NavLink';
import GameSelector from './GameSelector';
import SocialIcons from './SocialIcons';
import UserMenuPortal from './UserMenuPortal';
import NotificationBell from './NotificationBell';
import MobileMenu from './MobileMenu';

// UI
import Button from '../ui/Button';

// Assets
import SaltEmblem from '../../assets/salt-emblem.png';

// Styles
import '../../styles/navbar.css';
import '../../styles/notifications.css';

// —— Types ——————————————————————————————————————————

interface NavLinkDef {
  label: string;
  to: string;
  exact?: boolean;
}

// —— Component ——————————————————————————————————————

export default function Navbar() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthContext();
  const { gameId, gameCode, gameName, hasGame } = useGame();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const avatarBtnRef = useRef<HTMLButtonElement>(null);

  // —— Notifications (shared between Bell + MobileMenu) ——————

  const {
    unreadCount,
    notifications,
    isLoading: notificationsLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useNotifications({ enabled: isAuthenticated });

  // —— Navigation links ———————————————————————————————
  // Game-specific links only render on game subdomains (hasGame).
  // On bare domain, nav links are hidden — HomePage handles routing.

  const primaryLinks: NavLinkDef[] = hasGame
    ? [
        { label: 'Dashboard', to: '/', exact: true },
        { label: 'Leaderboard', to: '/leaderboard' },
        { label: 'Matches', to: '/matches' },
      ]
    : [];

  // —— Event handlers ———————————————————————————————————

  const handleLogoClick = () => {
    if (hasGame) {
      // On a game subdomain — navigate to bare domain (game selection)
      const protocol = window.location.protocol;
      const base = process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';
      // Strip the leading dot for bare domain: ".playsalt.gg" → "playsalt.gg"
      const bareDomain = base.startsWith('.') ? base.slice(1) : base;
      window.location.href = `${protocol}//${bareDomain}`;
    } else {
      navigate('/');
    }
  };

  const openAuth = () => navigate('/auth');

  const toggleMobile = () => setMobileOpen((prev) => !prev);
  const closeMobile = () => setMobileOpen(false);

  const toggleUserMenu = () => setUserMenuOpen((prev) => !prev);
  const closeUserMenu = () => setUserMenuOpen(false);

  const handleProfileClick = () => {
    if (!user?.userId) return;
    navigate(`/profile/${user.userId}`);
    closeUserMenu();
  };

  /**
   * "My Team / Create Team"
   *
   * Fetches the user profile overview to resolve the user's current
   * team for this game. The team info lives in competitiveSummary,
   * filtered by the current game's gameId (from subdomain context).
   * Navigates to the team profile if found, or to create-team if not.
   */
  const handleTeamClick = useCallback(async () => {
    try {
      if (!user?.userId) {
        navigate('/auth');
        return;
      }

      // Correct endpoint: GET /api/profile/:userId
      const res = await axiosClient.get(`/profile/${user.userId}`);

      // Team info is nested in competitiveSummary entries, one per game/season.
      // Find the entry matching the current game subdomain.
      const summary: Array<{
        gameId: number;
        team: { teamId: number };
      }> = res?.data?.competitiveSummary || [];

      // Use gameId from subdomain context (GameContext), not env var
      const currentGameEntry = gameId
        ? summary.find((entry) => entry.gameId === gameId)
        : summary[0] || null;

      if (currentGameEntry?.team?.teamId) {
        navigate(`/teams/${currentGameEntry.team.teamId}`);
      } else {
        navigate('/create-team');
      }
    } catch (err) {
      console.error('[Navbar] handleTeamClick error:', err);
      navigate('/create-team');
    } finally {
      closeUserMenu();
      closeMobile();
    }
  }, [navigate, user, gameId]);

  const handleLogoutClick = () => {
    if (typeof logout === 'function') logout();
    closeUserMenu();
    closeMobile();
    navigate('/auth');
  };

  // —— Notification navigation handler ————————————————————

  const handleNotificationNavigate = useCallback(
    (url: string) => {
      // actionUrl from backend may include ?gc=<gameCode> for cross-subdomain navigation.
      // If we're on a game subdomain already, strip the gc param and navigate normally.
      // If we're on the bare domain, redirect to the correct game subdomain.
      const parsed = new URL(url, window.location.origin);
      const gc = parsed.searchParams.get('gc');

      if (gc) {
        // Remove gc param from the path for clean navigation
        parsed.searchParams.delete('gc');
        const cleanPath = parsed.pathname + (parsed.search || '');

        // Validate gameCode against allowlist before redirecting
        const VALID_GAME_CODES = ['hi', 'h3'];
        if (!VALID_GAME_CODES.includes(gc.toLowerCase())) {
          // Invalid game code — navigate locally instead of redirecting
          navigate(cleanPath);
          return;
        }

        // Check if we're on a game subdomain already
        const host = window.location.hostname.toLowerCase();
        const isOnGameSub =
          /^[a-z0-9]+\.playsalt\.gg$/.test(host) ||
          /^[a-z0-9]+\.localhost/.test(host);

        if (!isOnGameSub) {
          // Bare domain — redirect to game subdomain
          const subBase = process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';
          const fullUrl = `${window.location.protocol}//${gc.toLowerCase()}${subBase}${cleanPath}`;
          window.location.href = fullUrl;
          return;
        }
        // Already on a game subdomain — just navigate normally
        navigate(cleanPath);
      } else {
        // No gc param — navigate as-is (profile URLs, admin URLs, etc.)
        navigate(url);
      }
    },
    [navigate]
  );

  // —— Derived display values ———————————————————————————

  const displayName = user?.username || user?.email || 'Player';
  const initials = displayName
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Close user menu when mobile opens
  useEffect(() => {
    if (mobileOpen) closeUserMenu();
  }, [mobileOpen]);

  return (
    <>
      <header className="sl-nav-shell">
        <div className="sl-nav">
          <div className="sl-nav-inner">
            {/* —— Left: Logo + Game Selector —— */}
            <div className="sl-nav-left">
              <div
                className="sl-nav-logo"
                onClick={handleLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleLogoClick();
                }}
              >
                <div className="sl-nav-logo-circle">
                  <img
                    src={SaltEmblem}
                    alt="SALT"
                    className="sl-nav-logo-img"
                  />
                </div>
                <div className="sl-nav-logo-text">
                  <span className="sl-nav-logo-title">SALT</span>
                  <span className="sl-nav-logo-subtitle">Series, Arenas, Leagues and Tournaments</span>
                </div>
              </div>

              {hasGame && (
                <GameSelector
                  currentCode={gameCode}
                  currentName={gameName}
                />
              )}
            </div>

            {/* —— Center: Desktop Nav Links —— */}
            {hasGame && (
              <nav className="sl-nav-links">
                {primaryLinks.map((link) => (
                  <NavLink key={link.label} to={link.to} exact={link.exact}>
                    {link.label}
                  </NavLink>
                ))}
              </nav>
            )}

            {/* —— Right: Social + Notifications + Auth —— */}
            <div className="sl-nav-right">
              <SocialIcons />

              <div className="sl-nav-social-divider" aria-hidden="true" />

              {/* Notification Bell (authenticated users only, desktop) */}
              <NotificationBell
                enabled={isAuthenticated}
                unreadCount={unreadCount}
                notifications={notifications}
                isLoading={notificationsLoading}
                onPanelOpen={fetchNotifications}
                onMarkRead={markAsRead}
                onMarkAllRead={markAllAsRead}
                onClearAll={clearAll}
                onNavigate={handleNotificationNavigate}
              />

              {isAuthenticated && user ? (
                <div className="sl-nav-desktop-user">
                  <button
                    ref={avatarBtnRef}
                    type="button"
                    className="sl-nav-avatar-button"
                    onClick={toggleUserMenu}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                  >
                    <div className="sl-nav-avatar-circle">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt=""
                          className="sl-nav-avatar-img"
                        />
                      ) : (
                        <span className="sl-nav-avatar-initials">{initials}</span>
                      )}
                    </div>
                    <span className="sl-nav-username">{displayName}</span>
                    <svg
                      className={`sl-nav-avatar-chevron ${userMenuOpen ? 'sl-nav-avatar-chevron--open' : ''}`}
                      width="10"
                      height="10"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 4.5L6 7.5L9 4.5" />
                    </svg>
                  </button>

                  <UserMenuPortal
                    open={userMenuOpen}
                    anchorRef={avatarBtnRef}
                    onClose={closeUserMenu}
                    onProfile={handleProfileClick}
                    onTeam={handleTeamClick}
                    onLogout={handleLogoutClick}
                    displayName={displayName}
                  />
                </div>
              ) : (
                <div className="sl-nav-auth-cta">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={openAuth}
                    className="sl-nav-auth-btn"
                  >
                    <span className="sl-auth-label sl-auth-label--full">
                      Login / Register
                    </span>
                    <span className="sl-auth-label sl-auth-label--short">
                      Login
                    </span>
                  </Button>
                </div>
              )}

              {/* Hamburger toggle (mobile only) */}
              <button
                type="button"
                className="sl-nav-mobile-toggle"
                onClick={toggleMobile}
                aria-label="Toggle navigation menu"
              >
                <span className="sl-nav-mobile-icon" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* —— Mobile Menu (portaled) —— */}
      <MobileMenu
        open={mobileOpen}
        onClose={closeMobile}
        primaryLinks={primaryLinks}
        currentGameCode={gameCode}
        isLoggedIn={isAuthenticated}
        onTeamClick={handleTeamClick}
        onLogoutClick={handleLogoutClick}
        unreadNotificationCount={unreadCount}
      />
    </>
  );
}